import React from 'react'
import { Link } from 'react-scroll'
import { Sticky } from 'react-stickup-ay'

import styled, { css } from 'styled-components'
import { white } from 'styles/legacyShared/constants/colors'
import { mediaTabletAndDown } from 'uiComponents/constants/customMedia'
import { fsL, fsXS, fwBold, fwLight } from 'uiComponents/constants/typography'
import SearchBar from '../SearchBar'

interface IProps {
  isSticky?: boolean
  getSearchRedirect?: (
    term: string,
  ) => Promise<{ url?: string; searchType: string } | undefined>
  updateSearchContext?: (term: string, searchType?: string) => void
}

type BrandSearchProps = {
  sticky: boolean
}

const BrandSearchContainer = styled.div`
  position: relative;
  z-index: 100;

  background-color: ${white};
  font-size: ${fsL}px;
  ${fwLight}
  letter-spacing: 0.8px;
  width: 100%;
  z-index: 999;

  ${(props: BrandSearchProps) =>
    props.sticky &&
    css`
      box-shadow: 0 2px 5px 0 rgba(0, 0, 0, 0.1);
      height: 100px;

      &::before {
        content: '';
        display: block;
        height: 20px;
        margin-top: -20px;
        background-color: ${white};
      }
    `}
  .brandNavLink {
    display: inline-block;
    cursor: pointer;
    padding: 0 13.5px;

    &:hover {
      ${fwBold}
    }

    &:hover .animatedUnderline::after {
      opacity: 1;
      width: 100%;
    }

    @media ${mediaTabletAndDown} {
      padding: 0 2.5px;
    }
  }

  .brandNavLinkActive,
  .brandNavLinkActive .animatedUnderline::after {
    ${fwBold}
  }

  .brandNavLinkActive .animatedUnderline::after {
    opacity: 1;
    width: 100%;
  }

  .animatedUnderline {
    position: relative;

    &::after {
      background: #16182f;
      content: '';
      height: 1px;
      left: 50%;
      margin-top: 2px;
      opacity: 0;
      position: absolute;
      top: 100%;
      transform: translateX(-50%);
      width: 0;
    }

    &.inverse::after {
      background: ${white};
    }
  }
`

const BrandNavLinkList = styled.ul`
  display: flex;
  flex-wrap: nowrap;
  justify-content: space-between;
  margin-top: 30px;
  width: 100%;
  ${(props: BrandSearchProps) =>
    props.sticky &&
    css`
      margin-top: 10px;

      @media ${mediaTabletAndDown} {
        padding: 0 5px;
      }
    `}
`

const BrandNavListItem = styled.li`
  font-size: ${fsXS}px;
  line-height: 16px;
  list-style: none outside none;
  text-transform: uppercase;
`

class BrandSearch extends React.Component<IProps, any> {
  render() {
    const { isSticky, getSearchRedirect, updateSearchContext } = this.props

    const alphabet = '#abcdefghijklmnopqrstuvwxyz'.split('')

    return (
      <BrandSearchContainer sticky={!!isSticky} id="brands">
        <div className="row">
          <SearchBar
            getSearchRedirect={getSearchRedirect}
            updateSearchContext={updateSearchContext}
          />
        </div>
        <div className="row">
          <BrandNavLinkList
            sticky={!!isSticky}
            data-testid="BrandOverviewAlphabeticalShortcuts"
          >
            {alphabet.map(letter => {
              return (
                <BrandNavListItem key={letter}>
                  <Link
                    activeClass="brandNavLinkActive"
                    className="brandNavLink"
                    to={`Letter${letter}`}
                    title={letter}
                    duration={500}
                    delay={100}
                    spy={true}
                    smooth={true}
                    isDynamic={true}
                    offset={-110}
                  >
                    <span className="animatedUnderline">{letter}</span>
                  </Link>
                </BrandNavListItem>
              )
            })}
          </BrandNavLinkList>
        </div>
      </BrandSearchContainer>
    )
  }
}

const StyledStickyWrapper = styled.div`
  .stickyPlaceholder {
    z-index: 1;
  }

  .stickyElement {
    width: inherit !important;
  }
`

const StickyBrandSearch: React.FC<{
  container?: React.RefObject<any>
  getSearchRedirect?: (
    term: string,
  ) => Promise<{ url?: string; searchType: string } | undefined>
  updateSearchContext?: (term: string, searchType?: string) => void
}> = ({ container, getSearchRedirect, updateSearchContext }) => {
  return (
    <StyledStickyWrapper>
      <Sticky
        container={container}
        className="stickyPlaceholder"
        stickyProps={{ className: 'stickyElement' }}
      >
        {stickyProps => (
          <BrandSearch
            {...stickyProps}
            getSearchRedirect={getSearchRedirect}
            updateSearchContext={updateSearchContext}
          />
        )}
      </Sticky>
    </StyledStickyWrapper>
  )
}

export default StickyBrandSearch
